import { useCallback } from "react";
import { UserLocation } from "../types/mapData";
import { Loader } from "@googlemaps/js-api-loader";

interface UseMapSetupProps {
  apiKey: string;
  setMap: (map: google.maps.Map) => void;
  setIsLoaded: (loaded: boolean) => void;
  setCenter: (
    latLng: google.maps.LatLngLiteral | google.maps.LatLng | null,
    offset?: "negative" | "offset",
  ) => void;
  widget: boolean;
  markersState: google.maps.Marker[];
  userLocation: UserLocation | null;
  handleAmplitude: (
    eventName: string,
    eventProperties?: Record<string, any>,
  ) => void;
  setUserLocation: (location: any) => void;
  setStaticLocation: (location: any) => void;
  isHamburgerRequired: boolean;
  hamburgerStateRef: React.MutableRefObject<boolean>;
  setHamburgerState: (state: boolean) => void;
}

export function useMapSetup({
  apiKey,
  setMap,
  setIsLoaded,
  setCenter,
  widget,
  markersState,
  userLocation,
  handleAmplitude,
  setUserLocation,
  setStaticLocation,
  isHamburgerRequired,
  hamburgerStateRef,
  setHamburgerState,
}: UseMapSetupProps) {
  // Internal helper function to handle initialization
  const _handleInit = useCallback(() => {
    const hamburgerMenu = document.getElementsByClassName("hamburger-menu")[0];
    const body = document.getElementsByTagName("body")[0];

    body.style.background = "transparent";

    if (isHamburgerRequired && hamburgerMenu) {
      hamburgerMenu.addEventListener("click", () => {
        setHamburgerState(!hamburgerStateRef.current);
      });
    }

    const url = window.location.hostname.includes("localhost")
      ? "http://app.localhost:3000/"
      : window.location.hostname.includes("-staging")
        ? "https://app.inkind-staging.com/"
        : "https://app.inkind.com/";

    fetch(`${url}api/v4/ip_location`)
      .then((response) => response.json())
      .then((ip) => {
        setUserLocation(ip);
        setStaticLocation(ip);
      })
      .catch(() => setUserLocation("not found"));
  }, [
    isHamburgerRequired,
    hamburgerStateRef,
    setHamburgerState,
    setUserLocation,
    setStaticLocation,
  ]);

  const handleStart = useCallback(async () => {
    const loader = new Loader({
      apiKey,
      version: "weekly",
      libraries: ["places", "geometry"],
    });
    try {
      await loader.importLibrary("maps");
      _handleInit();
    } catch (error) {
      console.error("Error loading Google Maps:", error);
    }
  }, [apiKey, _handleInit]);

  const handleSetMap = useCallback(() => {
    const maps = window.google?.maps;
    if (!maps || !userLocation?.location || typeof maps.LatLng !== "function") {
      console.error("Cannot set map - maps or userLocation missing");
      return;
    }

    const mapContainer = document.getElementById("map-container");
    if (!mapContainer) {
      console.error("Map container not found");
      return;
    }

    const [lat, lng] = userLocation.location;
    const center = new maps.LatLng(lat, lng);

    const mapOptions: google.maps.MapOptions = {
      center: { lat, lng },
      zoom: 8,
      clickableIcons: false,
      zoomControl: false,
      mapTypeControl: false,
      scaleControl: false,
      streetViewControl: false,
      rotateControl: false,
      fullscreenControl: false,
      gestureHandling: widget ? "auto" : "greedy",
      styles: [
        { featureType: "poi", stylers: [{ visibility: "off" }] },
        { featureType: "transit", stylers: [{ visibility: "off" }] },
      ],
    };

    const newMap = new maps.Map(mapContainer, mapOptions);

    // Reattach any existing markers to the new map instance
    if (markersState.length > 0) {
      markersState.forEach((marker) => marker.setMap(newMap));
    }

    // Track zooms + clicks for analytics
    newMap.addListener("zoom_changed", () =>
      handleAmplitude("click_web_map_content"),
    );
    maps.event.addListener(newMap, "click", () =>
      handleAmplitude("click_web_map_content"),
    );

    // Wait until tiles are loaded, then adjust center
    maps.event.addListenerOnce(newMap, "idle", () => {
      setCenter(center);
    });

    // Save map instance
    setMap(newMap);
    setIsLoaded(true);
  }, [
    userLocation,
    widget,
    markersState,
    handleAmplitude,
    setMap,
    setCenter,
    setIsLoaded,
  ]);

  return {
    handleStart,
    handleSetMap,
  };
}
